// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraDirection, CameraSource } from "@capacitor/camera";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { IGNORE_CAMERA_ERROR_MESSAGES } from "@src/appV2/lib/files/constants";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";

import { BADGE_VERIFICATION_IMAGE_WIDTH } from "../constants";
import { type BadgeVerificationPhoto } from "../types";

interface UseBadgeVerificationCameraParams {
  onPhotoTaken: (photo: BadgeVerificationPhoto) => void;
  onCameraPermissionDenied: () => void;
}

export function useBadgeVerificationCamera(params: UseBadgeVerificationCameraParams) {
  const { onCameraPermissionDenied } = params;
  const { showErrorToast } = useToast();

  return useTakePhoto({
    source: isCapacitorPlatform() ? CameraSource.Camera : CameraSource.Photos,
    onSuccess: (photoData: UploadFileData) => {
      params.onPhotoTaken({ ...photoData, previewUrl: photoData.dataUrlBase64 });
    },
    onError: (error) => {
      if (error instanceof CameraPermissionDeniedError) {
        onCameraPermissionDenied();
      } else if (IGNORE_CAMERA_ERROR_MESSAGES.has(error.message)) {
        // User cancelled camera - don't show error toast
      } else {
        showErrorToast("Failed to take photo. Please try again.");
      }
    },
    photoOptions: {
      direction: CameraDirection.Front,
      width: BADGE_VERIFICATION_IMAGE_WIDTH,
    },
  });
}
